import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const DigitalDownload = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const [fulfillment, setFulfillment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchFulfillmentInfo();
  }, [accessToken]);

  const fetchFulfillmentInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/digital/access/${accessToken}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError({
          code: data.code || 'UNKNOWN',
          message: data.error || 'Unable to access this download'
        });
        return;
      }

      setFulfillment(data.fulfillment);
    } catch (err) {
      console.error('Error fetching fulfillment:', err);
      setError({
        code: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/digital/download/${accessToken}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError({
          code: data.code || 'DOWNLOAD_ERROR',
          message: data.error || 'Unable to process download'
        });
        setDownloading(false);
        return;
      }

      // Open download URL in new tab
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        // Refresh the fulfillment info to update download count
        await fetchFulfillmentInfo();
      }
    } catch (err) {
      console.error('Error processing download:', err);
      setError({
        code: 'NETWORK_ERROR',
        message: 'Unable to process download. Please try again.'
      });
    } finally {
      setDownloading(false);
    }
  };

  const getErrorContent = () => {
    const errorConfig = {
      INVALID_TOKEN: {
        title: 'Invalid Download Link',
        description: 'This download link is not valid. Please check your email for the correct link.',
        icon: (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        )
      },
      REVOKED: {
        title: 'Access Revoked',
        description: 'Your access to this download has been revoked by the seller. Please contact them for assistance.',
        icon: (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        )
      },
      EXPIRED: {
        title: 'Download Link Expired',
        description: 'This download link has expired. Please contact the seller to request a new link.',
        icon: (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
        )
      },
      LIMIT_REACHED: {
        title: 'Download Limit Reached',
        description: 'You have reached the maximum number of downloads for this product. Please contact the seller if you need additional access.',
        icon: (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
          </svg>
        )
      }
    };

    return errorConfig[error?.code] || {
      title: 'Something Went Wrong',
      description: error?.message || 'An unexpected error occurred. Please try again later.',
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      )
    };
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9FAFB',
        padding: '20px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #E5E7EB',
          borderTop: '4px solid #0D7463',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '16px', color: '#6B7280' }}>Loading download information...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    const errorContent = getErrorContent();
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9FAFB',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '420px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ marginBottom: '24px' }}>
            {errorContent.icon}
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#111827',
            margin: '0 0 12px 0'
          }}>
            {errorContent.title}
          </h1>
          <p style={{
            fontSize: '15px',
            color: '#6B7280',
            margin: 0,
            lineHeight: '1.6'
          }}>
            {errorContent.description}
          </p>
        </div>
        <p style={{
          marginTop: '24px',
          fontSize: '13px',
          color: '#9CA3AF'
        }}>
          Powered by Revstar
        </p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F9FAFB',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Product Header */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          {fulfillment.productImage ? (
            <img
              src={fulfillment.productImage}
              alt={fulfillment.productName}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '12px',
              backgroundColor: '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </div>
          )}
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#111827',
              margin: '0 0 4px 0'
            }}>
              {fulfillment.productName}
            </h1>
            <span style={{
              display: 'inline-block',
              padding: '4px 10px',
              backgroundColor: fulfillment.canDownload ? '#D1FAE5' : '#FEE2E2',
              color: fulfillment.canDownload ? '#059669' : '#DC2626',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {fulfillment.canDownload ? 'Ready to Download' : 'Unavailable'}
            </span>
          </div>
        </div>

        {/* Download Details */}
        <div style={{
          display: 'grid',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {fulfillment.maxDownloads && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Downloads</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                {fulfillment.downloadCount} / {fulfillment.maxDownloads}
              </span>
            </div>
          )}

          {fulfillment.expiresAt && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Expires</span>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: fulfillment.isExpired ? '#DC2626' : '#111827'
              }}>
                {new Date(fulfillment.expiresAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* License Key */}
        {fulfillment.licenseKey && (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#F0FDF4',
            borderRadius: '12px',
            border: '1px solid #BBF7D0'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#166534',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              License Key
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <code style={{
                fontFamily: 'monospace',
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                letterSpacing: '0.05em'
              }}>
                {fulfillment.licenseKey}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(fulfillment.licenseKey)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #BBF7D0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#166534',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Download Button */}
        {fulfillment.canDownload ? (
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor: downloading ? '#9CA3AF' : '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: downloading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'background-color 150ms ease'
            }}
          >
            {downloading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Processing...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download Now
              </>
            )}
          </button>
        ) : (
          <button
            disabled
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'not-allowed'
            }}
          >
            {fulfillment.isExpired ? 'Link Expired' :
             fulfillment.limitReached ? 'Download Limit Reached' :
             !fulfillment.isActive ? 'Access Revoked' : 'Download Unavailable'}
          </button>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      <p style={{
        marginTop: '24px',
        fontSize: '13px',
        color: '#9CA3AF'
      }}>
        Powered by Revstar
      </p>
    </div>
  );
};

export default DigitalDownload;
